/* eslint-disable consistent-return */
import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import {
  useEffectiveSelectJobRouteState,
  useSelectJobFlowContext,
} from 'pages/SelectJob/SelectJobFlowContext';
import _get from 'lodash/get';
import Routes from 'lib/utils/routes';
import { useTranslation } from 'react-i18next';
import { toISO } from 'lib/utils/date';
import styled from 'styled-components';
import { palette } from '@m12s/component-library';

import useStartNewProductionRun from 'components/SwitchActivityTypeReview/useStartNewProductionRun';
import useEditLaborTicket from 'components/LaborTicket/hooks/useEditLaborTicket';
import useCreateLaborTicket from 'components/LaborTicket/hooks/useCreateLaborTicket';
import { useIsLaborTicketFlow } from 'lib/hooks/useIsLaborTicketFlow';

import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import { ACTIVITY_MODES } from 'lib/constants';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';
import { getMachine } from 'lib/selectors/getMachine';
import { getIsMESEnabled } from 'lib/selectors/getIsMESEnabled';
import { getEffectiveErpResources } from 'lib/selectors/getEffectiveErpResources';
import { getEffectiveErpResourceId } from 'lib/selectors/getEffectiveErpResourceId';
import {
  actionSetWorkOrderOpActionLoading,
  actionOptimisticActivitySetUpdate,
  actionCanonicalActivitySetUpdate,
} from 'lib/actions';
import { getPendingActivityTypeRef } from 'lib/selectors/getPendingActivityTypeRef';
import { UPDATE_ACTIVITY_SET } from 'lib/api/mutations';
import { throwErrorToast, throwSuccessToast } from 'lib/utils/toast';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getLatestActivitySetRef } from 'lib/selectors/getLatestActivitySetRef';
import { getWorkOrderId } from 'lib/selectors/getWorkOrderId';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getLatestActivityMode } from 'lib/selectors/getLatestActivityMode';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getLatestActivityModeStartAt } from 'lib/selectors/getLatestActivityModeStartAt';

import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { getLatestJob } from 'lib/selectors/getLatestJob';
import { ElevatedCard, ElevatedCardHover } from 'lib/utils/styled';
import ConfirmDialog from 'components/ConfirmDialog';
import { getMachineSchedule } from 'lib/selectors/getMachineSchedule';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getWorkOrderOpActionLoading } from 'lib/selectors/getWorkOrderOpActionLoading';
import { getSelectWorkOrderOpOptions } from 'lib/selectors/getSelectWorkOrderOp';
import useSelectActivity from 'lib/hooks/useSelectActivity';
import {
  postChangeJobForEvent,
  postSplitProgramEvents,
} from 'lib/api/multiJobSelectRest';
import useRequestJobStartAt from 'lib/hooks/useRequestJobStartAt';
import useRequestPartCountMultiplier from 'lib/hooks/useRequestPartCountMultiplier';
import useCreateActivitySetWithWorkOrder from '../../useCreateActivitySetWithWorkOrder';

import JobCardTitle from './JobCardTitle';
import JobCardDetails from './JobCardDetails';

const DefaultJobCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${palette.White};
  cursor: pointer;

  ${ElevatedCard}
  ${ElevatedCardHover}

  opacity: ${({ disabled }) => {
    return disabled ? '0.65' : '1';
  }};

  ${({ disabled }) => {
    return (
      disabled &&
      `
        &:hover {
          cursor: pointer;
        }
      `
    );
  }}

  ${({ excluded }) => {
    return (
      excluded &&
      `
        animation: disappear 1s forwards;
        @keyframes disappear {
          to {
            opacity: 0;
            visibility: hidden;
            display: none;
          }
        }
      `
    );
  }}
`;

const StandardJobCardContainer = styled(DefaultJobCardContainer)`
  width: 17rem;
  flex-shrink: 0;
  height: 100%;
  min-height: 25rem;
`;

const SearchJobCardContainer = styled(DefaultJobCardContainer)`
  width: 100%;
  height: fit-content;
`;

const JobCard = ({ workOrderOp, nextStep, dispatch, exclude = false }) => {
  const mainDispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const selectJobFlowCtx = useSelectJobFlowContext();
  const effectiveRouteState = useEffectiveSelectJobRouteState(
    history.location.state
  );
  const queryParams = queryString.parse(history.location.search);
  const laborTicketEnabled = useIsLaborTicketFlow();
  const createLaborTicket = useCreateLaborTicket();

  const [
    notAssignedToMachineDialogOpen,
    setNotAssignedToMachineDialogOpen,
  ] = useState(false);
  const isSubmittingRef = useRef(false);
  const machineSchedule = useSelector(getMachineSchedule);

  const machine = useSelector(getMachine);
  const erpResourceId = useSelector(getEffectiveErpResourceId);
  const activityTypes = useSelector(getActivityTypes);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const activitySetRef = useSelector(getLatestActivitySetRef);
  const isAPMEnabled = useSelector(getIsAPMEnabled);
  const isMESEnabled = useSelector(getIsMESEnabled);
  const effectiveErpResources = useSelector(getEffectiveErpResources);
  const latestActivityMode = useSelector(getLatestActivityMode);
  const latestActivityModeStartAt = useSelector(getLatestActivityModeStartAt);
  const currentOperator = useSelector(getCurrentOperator);
  const { isSearchMode } = useSelector(getSelectWorkOrderOpOptions);
  const workOrderOpActionLoading = useSelector(getWorkOrderOpActionLoading);

  const pendingActivityTypeRef = useSelector(getPendingActivityTypeRef);
  const latestWorkOrderId = useSelector(getWorkOrderId);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const isUpdate = !!queryParams.updateWorkOrderId;

  const latestJob = useSelector(getLatestJob);
  const latestActivity = useSelector(getLatestActivity);

  const { workOrder, workOrderOperationRef } = workOrderOp;

  const workOrderId = _get(workOrder, 'workOrderId');
  const setupActivityTypes = activityTypes.filter((type) => {
    return type.activityMode === ACTIVITY_MODES.SETUP;
  });
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);
  const shouldStartAsSetup =
    !isJobLaborTrackingEnabled && setupActivityTypes?.length === 1;
  const { openSelectActivity, selectActivityOpen } = useSelectActivity();

  const startNewProductionRun = useStartNewProductionRun();
  const editLaborTicket = useEditLaborTicket();

  const requestJobStartAt = useRequestJobStartAt();
  const requestPartCountMultiplier = useRequestPartCountMultiplier();
  const requestCreateActivitySetWithWorkOrder = useCreateActivitySetWithWorkOrder(
    {
      machine,
      workOrderOp,
    }
  );

  /**
   * Wrapper function for handling the next step in the workflow
   * For ERP job control bar flow:
   * - Opens activity selection if not already open and machine exists
   * - Uses matching part operation from work order or falls back to work order op data
   * For non-ERP flow:
   * - Simply calls the nextStep function
   */
  const handleJobSelected = useCallback(
    async ({ partOperation }) => {
      if (!isJobLaborTrackingEnabled) {
        const selectedPartOperation =
          workOrderOp?.matchingPartOperations?.length > 0
            ? workOrderOp.matchingPartOperations[0]
            : partOperation;

        const activityTypeRef =
          latestJob?.partOperationRef ===
          selectedPartOperation?.partOperationRef
            ? latestActivity.activityTypeRef
            : pendingActivityTypeRef;

        try {
          // Conditionally get startAt based on isUnknownJob status
          const startAt =
            (activityTypeRef || shouldStartAsSetup) &&
            (await requestJobStartAt());

          const defaultPartCountMultiplier =
            workOrderOp?.quantityPerPart ||
            workOrderOp?.quantityPerCycle ||
            partOperation?.quantityPerPart ||
            1;
          const partCountMultiplier = await requestPartCountMultiplier(
            defaultPartCountMultiplier,
            activityTypeRef ?? setupActivityTypes[0]?.activityTypeRef
          );

          dispatch(actionSetWorkOrderOpActionLoading(true));

          if (activityTypeRef) {
            return requestCreateActivitySetWithWorkOrder({
              activityTypeRef,
              partOperation,
              startAt,
              partCountMultiplier,
            });
          }

          if (shouldStartAsSetup) {
            return requestCreateActivitySetWithWorkOrder({
              activityTypeRef: setupActivityTypes[0].activityTypeRef,
              partOperation,
              startAt,
              partCountMultiplier,
            });
          }
        } catch (error) {
          if (error?.code !== 'USER_CANCELLED') {
            throwErrorToast(t('Failed to start job'));
          }
          return null;
        }
      }

      dispatch({
        type: 'SET_PART_OPERATION',
        partOperation,
      });

      if (isJobLaborTrackingEnabled) {
        if (!selectActivityOpen && machine) {
          openSelectActivity({
            workOrderOp,
            partOperation,
          });
        }
      } else {
        nextStep();
      }
    },
    [
      isJobLaborTrackingEnabled,
      selectActivityOpen,
      machine,
      nextStep,
      pendingActivityTypeRef,
      setupActivityTypes,
      latestActivity,
      latestJob,
      dispatch,
      openSelectActivity,
      requestCreateActivitySetWithWorkOrder,
      shouldStartAsSetup,
      workOrderOp,
      requestJobStartAt,
      requestPartCountMultiplier,
      t,
    ]
  );

  const [requestUpdateActivitySet] = useMutation(UPDATE_ACTIVITY_SET, {
    fetchPolicy: 'no-cache',
    update: (
      _cache,
      {
        data: {
          updateActivitySetResponse: { activitySet },
        },
      }
    ) => {
      mainDispatch(actionOptimisticActivitySetUpdate({ activitySet }));
    },
    onCompleted: async ({ updateActivitySetResponse: { activitySet } }) => {
      await mainDispatch(actionCanonicalActivitySetUpdate({ activitySet }));
      await mainDispatch(actionSetWorkOrderOpActionLoading(false));
      throwSuccessToast(`Changed to "${activitySet.workOrderId}"`);

      return history.push({
        pathname: Routes.machineIdHomePath(machine.id),
      });
    },
    onError: () => {
      mainDispatch(
        actionCanonicalActivitySetUpdate({
          activitySet: {
            ...latestActivitySet,
            workOrderId: latestWorkOrderId,
          },
        })
      );
      mainDispatch(actionSetWorkOrderOpActionLoading(false));
      throwErrorToast(t('Failed to update production order'));
    },
  });

  const handleStartAndUpdateManual = () => {
    const partOperation = workOrderOp.matchingPartOperations
      ? workOrderOp.matchingPartOperations[0]
      : {
          // if no matchingPartOperations, it means its coming from the produciton schedule endpoint, values are included directly on woop
          ...workOrderOp,
        };

    if (partOperation?.partOperationRef) {
      if (!isAPMEnabled && isUpdate) {
        mainDispatch(actionSetWorkOrderOpActionLoading(true));
        return requestUpdateActivitySet({
          variables: {
            input: {
              machineRef: machine?.machineRef,
              activitySetRef,
              workOrderId,
              workOrderOperationRef,
              partNumber: partOperation?.partNumber,
              sequenceNumber: partOperation?.sequenceNumber,
              partOperationRef: partOperation.partOperationRef,
            },
            query: { updateFromWorkOrder: 'true' },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateActivitySetResponse: {
              __typename: 'ActivitySet',
              activitySet: {
                ...latestActivitySet,
                workOrderId,
                workOrderOperationRef,
              },
            },
          },
        });
      }

      return handleJobSelected({ partOperation });
    }
    mainDispatch(actionSetWorkOrderOpActionLoading(false));
    return throwErrorToast('Unable to find part operation');
  };

  const active =
    workOrderOp &&
    currentWorkOrderOp?.workOrderOperationRef ===
      workOrderOp?.workOrderOperationRef;
  // The currently-running job is a valid target when changing a run's job
  // (reassigning its intervals to it), so it should stay selectable in the
  // change-job flow. Only disable the active job in the start/add-job flow.
  const isChangeJobFlow = effectiveRouteState?.eventId != null;
  const disabled =
    (active && !isChangeJobFlow) || (isUpdate && !latestActivitySet);

  const CardContainer = isSearchMode
    ? SearchJobCardContainer
    : StandardJobCardContainer;

  const handleSelectJob = async () => {
    dispatch({
      type: 'SET_WORK_ORDER_OPERATION',
      workOrderOp,
    });

    if (queryParams.activityTypeRef) {
      return startNewProductionRun(
        queryParams.activityTypeRef,
        workOrder.workOrderId,
        workOrderOperationRef
      );
    }
    if (isUpdate) {
      // EDIT / ADD ORDER OPERATION
      mainDispatch(actionSetWorkOrderOpActionLoading(true));
      if (laborTicketEnabled) {
        if (currentLaborTicket) {
          await editLaborTicket(
            {
              lot: workOrderOp.lot,
              sequenceNumber: workOrderOp.sequenceNumber,
              split: workOrderOp.split,
              sub: workOrderOp.sub,
              workOrderId: workOrderOp.workOrderId,
            },
            currentLaborTicket.laborTicketRef
          );
        } else {
          const clockIn = toISO(
            Math.max(
              moment().valueOf(),
              latestActivityModeStartAt
                ? moment(latestActivityModeStartAt).valueOf()
                : 0
            )
          );

          await createLaborTicket({
            machineRef: machine?.machineRef,
            clockIn,
            clockOut: null,
            lot: workOrderOp.lot,
            sequenceNumber: workOrderOp.sequenceNumber,
            split: workOrderOp.split,
            sub: workOrderOp.sub,
            state: 'OPEN',
            resourceId: erpResourceId,
            personId: currentOperator.erpId,
            workOrderId: workOrderOp.workOrderId,
            type: latestActivityMode,
            goodParts: 0,
            badParts: 0,
          });
        }
      }

      if (isAPMEnabled) {
        // ONLY UPDATE FOR ID/REF FOR APM
        return requestUpdateActivitySet({
          variables: {
            input: {
              machineRef: machine?.machineRef,
              activitySetRef,
              workOrderId,
              workOrderOperationRef,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateActivitySetResponse: {
              __typename: 'ActivitySet',
              activitySet: {
                ...latestActivitySet,
                workOrderId,
                workOrderOperationRef,
              },
            },
          },
        });
      }
    }

    if (isAPMEnabled) {
      if (effectiveRouteState?.isSplitChangeJob) {
        const splitCtx = selectJobFlowCtx?.splitJobContext;
        // Picking a job only CHOOSES the destination — the split review
        // reopens with it and the operator confirms there.
        if (splitCtx && selectJobFlowCtx?.onSplitJobSelected) {
          selectJobFlowCtx.onSplitJobSelected({
            workOrderOperationRef,
            workOrderId:
              workOrderOp?.workOrderId ?? workOrder?.workOrderId ?? null,
            partNumber: workOrderOp?.partNumber ?? null,
            sequenceNumber: workOrderOp?.sequenceNumber ?? null,
            finishQuantity:
              workOrderOp?.finishQuantity ??
              workOrderOp?.quantityScheduled ??
              null,
          });
          return;
        }
        if (splitCtx) {
          try {
            await postSplitProgramEvents(
              splitCtx.machineRef,
              splitCtx.splitTime,
              splitCtx.workOrderOperationRef,
              workOrderOperationRef
            );
          } catch {
            throwErrorToast(t('Failed change job'));
            return;
          }
        }
        if (selectJobFlowCtx?.onApmFlowComplete) {
          selectJobFlowCtx.onApmFlowComplete();
        } else {
          history.push(Routes.machineIdHomePath(machine.id));
        }
        return;
      }

      const eventId = effectiveRouteState?.eventId;

      if (eventId) {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        mainDispatch(actionSetWorkOrderOpActionLoading(true));
        try {
          await postChangeJobForEvent(
            machine.machineRef,
            eventId,
            workOrderOperationRef,
            effectiveRouteState?.sourceEventRefs
          );
          throwSuccessToast(`Changed to "${workOrderId}"`);
          window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
          if (selectJobFlowCtx?.onApmFlowComplete) {
            selectJobFlowCtx.onApmFlowComplete();
          } else {
            history.push(Routes.machineIdHomePath(machine.id));
          }
        } catch {
          throwErrorToast(t('Failed to change job'));
        } finally {
          isSubmittingRef.current = false;
          mainDispatch(actionSetWorkOrderOpActionLoading(false));
        }
      } else {
        const partOperation = workOrderOp.matchingPartOperations
          ? workOrderOp.matchingPartOperations[0]
          : { ...workOrderOp };

        openSelectActivity({
          workOrderOp,
          partOperation,
          isApmStartJob: true,
        });
        if (selectJobFlowCtx?.onApmFlowComplete) {
          selectJobFlowCtx.onApmFlowComplete();
        } else {
          history.push(Routes.machineIdHomePath(machine.id));
        }
      }
      return;
    }

    // MANUAL MACHINE - START / UPDATE BASED ON ORDER OPERATION
    // Note: actionSetWorkOrderOpActionLoading is set inside handleJobSelected,
    // after any user-facing dialogs (start time, part count multiplier) have resolved.
    return handleStartAndUpdateManual();
  };

  const handleConfirmMachineAndSelectJob = async () => {
    if (
      disabled ||
      exclude === true ||
      isSubmittingRef.current ||
      workOrderOpActionLoading
    ) {
      return;
    }

    if (isAPMEnabled) {
      handleSelectJob();
      return;
    }

    const isScheduledInProductionSchedule = machineSchedule?.some(
      (scheduledWorkOrderOp) => {
        return (
          workOrderOp?.workOrderOperationRef ===
          scheduledWorkOrderOp?.workOrderOperationRef
        );
      }
    );

    // TODO: resource.machineRef is being deprecated. The non-MES fallback checks
    // resource.machineRef and resource.resourceId membership; both will be replaced by
    // machineSettings.laborResourceId / labor ticket machineRef once that relationship is live.
    const isScheduledInERP = isMESEnabled
      ? effectiveErpResources.includes(workOrderOp?.resourceId)
      : workOrderOp?.resource?.machineRef === machine?.machineRef ||
        effectiveErpResources.includes(workOrderOp?.resource?.resourceId);

    if (isScheduledInProductionSchedule || isScheduledInERP) {
      handleSelectJob();
    } else {
      setNotAssignedToMachineDialogOpen(true);
    }
  };

  return (
    <>
      {notAssignedToMachineDialogOpen && (
        <ConfirmDialog
          title={t('Job not assigned to this machine')}
          message={t(
            'The job you have selected is not assigned to this machine. Would you like to start it anyways?'
          )}
          confirmText={t('Start Job')}
          cancelText={t('Cancel')}
          onConfirm={() => {
            setNotAssignedToMachineDialogOpen(false);
            handleSelectJob();
          }}
          onCancel={() => {
            setNotAssignedToMachineDialogOpen(false);
          }}
        />
      )}

      <CardContainer
        excluded={exclude}
        isSearchMode={isSearchMode}
        key={workOrderOperationRef}
        disabled={disabled}
        onClick={handleConfirmMachineAndSelectJob}
      >
        <JobCardTitle workOrderOp={workOrderOp} active={active} />
        <JobCardDetails workOrderOp={workOrderOp} active={active} />
      </CardContainer>
    </>
  );
};

JobCard.propTypes = {
  workOrderOp: PropTypes.object,
  nextStep: PropTypes.func,
  dispatch: PropTypes.func,
};

export default JobCard;
